import React, { useEffect, useState } from "react";
import { settingsService } from "../../services/api/settings";

interface DocumentTypeProps {
  category: string;
  setCategory: (v: string) => void;
  subType: string;
  setSubType: (v: string) => void;
  onChange: (data: { category: string; subType: string; domain?: string; domainId?: number; documentTypeId?: number; documentSubtypeId?: number }) => void;
}

const DocumentType: React.FC<DocumentTypeProps> = ({
  category,
  setCategory,
  subType,
  setSubType,
  onChange,
}) => {
  const [domains, setDomains] = useState<any[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<string>("");
  const [selectedDomainId, setSelectedDomainId] = useState<number | null>(null);
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>("");
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState<number | null>(null);
  const [documentSubtypes, setDocumentSubtypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Charger les domaines au montage
  useEffect(() => {
    const loadDomains = async () => {
      try {
        setLoading(true);
        const domainsData = await settingsService.getDomains();
        setDomains(domainsData);
      } catch (error) {
        console.error("Erreur lors du chargement des domaines:", error);
      } finally {
        setLoading(false);
      }
    };
    loadDomains();
  }, []);

  // Charger les types de documents quand un domaine est sélectionné
  useEffect(() => {
    const loadDocumentTypes = async () => {
      if (!selectedDomainId) {
        setDocumentTypes([]);
        return;
      }

      try {
        const typesData = await settingsService.getDocumentTypes(selectedDomainId);
        setDocumentTypes(typesData);
      } catch (error) {
        console.error("Erreur lors du chargement des types:", error);
        setDocumentTypes([]);
      }
    };

    loadDocumentTypes();
  }, [selectedDomainId]);

  // Trouver le domaine sélectionné
  const selectedDomainObj = domains.find(d => d.code === selectedDomain);
  const isTransversalDomain = selectedDomainObj?.is_transversal;

  // Charger les documents clés (sous-types) quand un type est sélectionné OU pour le domaine transversal
  useEffect(() => {
    const loadDocumentSubtypes = async () => {
      // Pour le domaine transversal, charger directement les sous-types du type "Transversal"
      if (isTransversalDomain && selectedDomainId) {
        try {
          // Trouver le type "Transversal" pour ce domaine
          const transversalType = documentTypes.find(t => t.name === "Transversal");
          if (transversalType) {
            const subtypesData = await settingsService.getDocumentSubtypes(transversalType.id);
            setDocumentSubtypes(subtypesData);
            setSelectedDocumentTypeId(transversalType.id);
            setSelectedDocumentType("Transversal");
            setCategory("Transversal");
          }
        } catch (error) {
          console.error("Erreur lors du chargement des documents transversaux:", error);
          setDocumentSubtypes([]);
        }
      } else if (selectedDocumentTypeId) {
        // Pour les domaines métier, charger les sous-types du type sélectionné
        try {
          const subtypesData = await settingsService.getDocumentSubtypes(selectedDocumentTypeId);
          setDocumentSubtypes(subtypesData);
        } catch (error) {
          console.error("Erreur lors du chargement des documents clés:", error);
          setDocumentSubtypes([]);
        }
      } else {
        setDocumentSubtypes([]);
      }
    };

    loadDocumentSubtypes();
  }, [selectedDocumentTypeId, isTransversalDomain, selectedDomainId, documentTypes, selectedDomainObj]);

  // Gérer le changement de domaine
  const handleDomainChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const domainCode = e.target.value;
    setSelectedDomain(domainCode);
    
    const domain = domains.find(d => d.code === domainCode);
    if (domain) {
      setSelectedDomainId(domain.id);
      setSelectedDocumentType("");
      setSelectedDocumentTypeId(null);
      setDocumentSubtypes([]);
      setCategory("");
      setSubType("");
    }
  };

  // Gérer le changement de type (pour les domaines métier)
  const handleDocumentTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const typeId = parseInt(e.target.value);
    setSelectedDocumentTypeId(typeId);
    
    const type = documentTypes.find(t => t.id === typeId);
    if (type) {
      setSelectedDocumentType(type.name);
      setCategory(type.name);
      setSubType("");
      setDocumentSubtypes([]);
    }
  };

  // Gérer le changement de document clé (sous-type)
  const handleSubtypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const subtypeId = parseInt(e.target.value);
    const subtype = documentSubtypes.find(st => st.id === subtypeId);
    
    if (subtype) {
      setSubType(subtype.name);
      // Notifier le parent avec toutes les informations
      onChange({
        category: isTransversalDomain ? "Transversal" : selectedDocumentType,
        subType: subtype.name,
        domain: selectedDomain,
        domainId: selectedDomainId || undefined,
        documentTypeId: selectedDocumentTypeId || undefined,
        documentSubtypeId: subtypeId
      });
    }
  };

  if (loading) {
    return (
      <div className="bg-white/60 border border-gray-200 rounded-xl p-4 mb-2">
        <p className="text-sm text-gray-600">Chargement des domaines...</p>
      </div>
    );
  }

  return (
    <div className="bg-white/60 border border-gray-200 rounded-xl p-2 mb-2">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Domaine */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-0.5">
            Domaine <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <select
              value={selectedDomain}
              onChange={handleDomainChange}
              className="w-full p-1.5 border rounded-lg bg-white"
              title={selectedDomainObj?.name || ""}
            >
              <option value="">Sélectionnez un domaine...</option>
              {domains.map((domain) => (
                <option key={domain.id} value={domain.code} title={domain.name}>
                  {domain.icon} {domain.code}
                </option>
              ))}
            </select>
            {selectedDomainObj && (
              <p className="text-xs text-gray-500 mt-1">
                {selectedDomainObj.name}
              </p>
            )}
          </div>
        </div>

        {/* Type (seulement pour les domaines métier) */}
        {selectedDomain && !isTransversalDomain && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-0.5">
              Type/Catégorie <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedDocumentTypeId || ""}
              onChange={handleDocumentTypeChange}
              className="w-full p-1.5 border rounded-lg bg-white"
              disabled={!selectedDomainId}
            >
              <option value="">
                {selectedDomainId ? "Sélectionnez un type..." : "Choisissez un domaine d'abord"}
              </option>
              {documentTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Document clé (sous-type) */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-0.5">
            Document clé <span className="text-red-500">*</span>
          </label>
          <select
            value={documentSubtypes.find(st => st.name === subType)?.id || ""}
            onChange={handleSubtypeChange}
            className="w-full p-1.5 border rounded-lg bg-white"
            disabled={isTransversalDomain ? !selectedDomainId : !selectedDocumentTypeId}
          >
            <option value="">
              {isTransversalDomain
                ? selectedDomainId
                  ? "Sélectionnez un document..."
                  : "Choisissez un domaine d'abord"
                : selectedDocumentTypeId
                ? "Sélectionnez un document clé..."
                : "Choisissez un type d'abord"}
            </option>
            {documentSubtypes.map((subtype) => (
              <option key={subtype.id} value={subtype.id}>
                {subtype.name}
              </option>
            ))}
          </select>
          {!subType && (isTransversalDomain ? selectedDomainId : selectedDocumentTypeId) && (
            <p className="text-xs text-red-500 mt-1">
              Sélectionnez un document clé pour continuer.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentType;
